// Shared helpers for postcard template patterns (postcard_pattern_type JSON).
// The JSON shape is rendered server-side by PostcardGenerationService, so the
// fields here must stay in sync with it.

export const QR_TOKEN = '{unique_url_qr}'

export const PATTERN_VARIABLES = [
	{ token: '{name}', description: 'Recipient name' },
	{ token: '{address}', description: 'Mailing address' },
	{ token: '{city}', description: 'City' },
	{ token: '{state}', description: 'State abbreviation' },
	{ token: '{zip}', description: 'ZIP code' },
	{ token: '{unique_url}', description: 'Unique tracked link for this recipient' },
	{ token: QR_TOKEN, description: 'QR code that opens the unique tracked link' },
]

const SAMPLE_VALUES = {
	'{name}': 'City of Sample',
	'{address}': '123 Main Street',
	'{city}': 'Anytown',
	'{state}': 'NY',
	'{zip}': '10001',
	'{country}': 'US',
	'{description}': '',
	// Printed short link; the QR code opens the same link with a trailing "q".
	'{unique_url}': 'twn.link/a7k2m',
}

export function patternPreviewText(text) {
	return Object.entries(SAMPLE_VALUES).reduce((value, [token, sample]) => value.split(token).join(sample), text || '')
}

export function previewFontFamily(fontFamily) {
	return fontFamily === 'PlusJakartaSans' ? 'PlusJakartaSans, "Plus Jakarta Sans"' : fontFamily
}

/** Weight 900 ("Black") is ExtraBold plus a same-color stroke; mirrors PostcardGenerationService::BLACK_STROKE_RATIO. */
export const BLACK_STROKE_RATIO = 0.01

/** CSS -webkit-text-stroke for the preview, or null when the pattern isn't Black. */
export function patternTextStroke(pattern, displayFontSize) {
	if (Number(pattern.fontWeight) < 900) return null
	const fontSize = pattern.fontSize || 48
	// The server stamps the text within a whole-pixel radius; a centered CSS stroke needs twice that width.
	const radius = Math.max(1, Math.round(fontSize * BLACK_STROKE_RATIO))
	return `${((radius * 2 * displayFontSize) / fontSize).toFixed(2)}px ${pattern.color || '#000000'}`
}

/** Measure a text pattern with canvas; the server uses these for alignment. */
export function measurePattern(pattern) {
	const ctx = document.createElement('canvas').getContext('2d')
	const fontSize = pattern.fontSize || 48
	ctx.font = `${pattern.fontWeight || '400'} ${fontSize}px ${previewFontFamily(pattern.fontFamily || 'PlusJakartaSans')}`
	pattern.patternWidth = ctx.measureText(pattern.text).width
	pattern.patternHeight = fontSize * 1.2
	return pattern
}

export function newPattern(text, x, y) {
	const isQr = text === QR_TOKEN
	return measurePattern({
		text,
		x: Math.min(Math.max(x, 0), 95),
		y: Math.min(Math.max(y, 0), 95),
		fontSize: 48,
		color: '#000000',
		fontFamily: 'PlusJakartaSans',
		fontWeight: '400',
		qrSize: isQr ? 220 : null,
		coordinateVersion: 2,
		patternHeight: 0,
		patternWidth: 0,
	})
}

/** postcard_pattern_type JSON → { front: [], back: [] } editor patterns. */
export function parsePatterns(json) {
	const sides = { front: [], back: [] }
	if (!json) return sides

	let all = []
	try {
		all = JSON.parse(json) || []
	} catch (error) {
		console.error('Error parsing postcard_pattern_type JSON:', error)
		return sides
	}

	all.forEach(pattern => {
		if (!sides[pattern.image]) return
		const data = {
			text: pattern.text,
			x: pattern.position.x,
			y: pattern.position.y,
			fontSize: pattern.fontSize || 18,
			fontWeight: pattern.fontWeight || '400',
			qrSize: pattern.qrSize || (pattern.text === QR_TOKEN ? 220 : null),
			coordinateVersion: pattern.coordinateVersion || 1,
			color: pattern.color || '#000000',
			fontFamily: pattern.fontFamily || 'PlusJakartaSans',
			patternHeight: pattern.patternHeight || 0,
			patternWidth: pattern.patternWidth || 0,
		}
		sides[pattern.image].push(data.patternWidth && data.patternHeight ? data : measurePattern(data))
	})

	return sides
}

/** { front, back } editor patterns → postcard_pattern_type array (null when empty). */
export function buildPatterns(front = [], back = []) {
	const toJson = side => (pattern, index) => ({
		image: side,
		index,
		text: pattern.text,
		position: { x: pattern.x, y: pattern.y },
		fontSize: pattern.fontSize,
		fontWeight: pattern.fontWeight,
		qrSize: pattern.qrSize,
		coordinateVersion: pattern.coordinateVersion || 1,
		color: pattern.color,
		fontFamily: pattern.fontFamily,
		patternHeight: pattern.patternHeight,
		patternWidth: pattern.patternWidth,
	})

	return [...front.map(toJson('front')), ...back.map(toJson('back'))]
}

export function patternsUseUniqueUrl(patterns) {
	return patterns.some(pattern => (pattern.text || '').includes('{unique_url'))
}

/** Load the TTFs the server renders with, so the editor preview matches print. */
export async function loadTemplateFonts() {
	if (!document.fonts || window.__mc2TemplateFontsLoaded) return
	const apiBase = process.env.NODE_ENV === 'production' ? 'https://mc2api.townweb.com' : 'http://127.0.0.1:8000'
	const weights = { 400: 'Regular', 500: 'Medium', 600: 'SemiBold', 700: 'Bold', 800: 'ExtraBold' }
	const loaded = await Promise.all(
		Object.entries(weights).map(async ([weight, fileWeight]) => {
			try {
				const font = new FontFace('PlusJakartaSans', `url(${apiBase}/storage/fonts/PlusJakartaSans/PlusJakartaSans-${fileWeight}.ttf)`, { weight })
				await font.load()
				document.fonts.add(font)
				return true
			} catch (error) {
				console.warn(`Could not load PlusJakartaSans ${weight} for template preview`, error)
			}
		})
	)
	window.__mc2TemplateFontsLoaded = loaded.some(Boolean)
}
